import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/SubscriptionItemsService';
import { SubscriptionItem } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useSubscriptionItem = (subscriptionId: string, subscriptionItemId?: string) => {
    if (!subscriptionId) {
        throw new Error('subscriptionId is required parameter');
    }

    const queryClient = useQueryClient();

    const subscriptionItemQuery = useQuery({
        queryKey: ['subscription-item', subscriptionId, subscriptionItemId],
        queryFn: () => fetch(subscriptionItemId!),
        enabled: !!subscriptionItemId
    });

    const createMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add({ ...rest, subscriptionId });
        },
        onSuccess: () => {
            notifySuccess('Subscription Item created');
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
            queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            if (!subscriptionItemId) throw new Error('subscriptionItemId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(subscriptionItemId, rest);
        },
        onSuccess: () => {
            notifySuccess('Subscription Item updated');
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
            queryClient.invalidateQueries({ queryKey: ['subscription-item', subscriptionId, subscriptionItemId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!subscriptionItemId) throw new Error('subscriptionItemId is required parameter');
            return deleteRecord(subscriptionItemId);
        },
        onSuccess: () => {
            notifySuccess('Subscription Item deleted');
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    return {
        record: subscriptionItemQuery.data,
        loading: subscriptionItemQuery.isLoading,
        fetchRecord: subscriptionItemQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};